import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, ArrowRight, Linkedin, Twitter, Facebook, Instagram, Compass, Target, Building, Ruler, Layers, PenTool } from 'lucide-react';
import { useState } from 'react';
import { LOGO_URL, CONTACT_INFO, mainServices, aboutLinks } from '../../data/serviceConfig';
import TypeWriter from '../TypeWriter';
import ParticleNetwork from '../animations/ParticleNetwork';
import FooterSeoLinks from './FooterSeoLinks';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const quickLinks = [
  { name: 'Get Started', href: '/get-started' },
  { name: 'Get a Quote', href: '/get-quote' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Service Areas', href: '/service-areas' },
  { name: 'Orlando Office', href: '/orlando-cad-drafting-services' },
  { name: 'Blog', href: '/about/blog' },
  { name: 'Answers', href: '/answers' },
  { name: 'Portfolio', href: '/portfolio' },
  { name: 'Contact Us', href: '/contact' },
  { name: 'Privacy Policy', href: '/privacy' },
  { name: 'Terms of Service', href: '/terms' },
];

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${API_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setSubscribed(true);
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
    }
  };

  return (
    <>
      {/* Pre-Footer CTA Section */}
      <section className="bg-primary text-white section-spacing relative overflow-hidden">
        <ParticleNetwork color="#ffffff" lineColor="#ffffff" particleCount={55} maxDistance={120} speed={0.3} />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-6">
              <TypeWriter texts={[
                [{text:"Let's ", cls:''}, {text:'Grow', cls:'text-orange-400 italic'}, {text:' Together', cls:''}],
                [{text:'Partner with the ', cls:''}, {text:'Best', cls:'text-green-300 italic'}],
              ]} speed={55} deleteSpeed={30} pauseTime={2500} />
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-lg md:text-xl mb-8 text-white/80">
              Ready to transform your CAD drafting workflow? Partner with Land Drafters LLC for precision, reliability, and excellence.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/get-quote" className="btn-orange flex items-center justify-center gap-2" data-testid="footer-cta-quote">
                Get a Quote <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="bg-white text-primary hover:bg-white/90 uppercase tracking-widest font-bold px-8 py-4 text-sm transition-all flex items-center justify-center gap-2" data-testid="footer-cta-contact">
                Contact Us <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
        <div className="absolute top-0 left-0 w-64 h-64 border border-white/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 border border-white/10 rounded-full translate-x-1/2 translate-y-1/2" />
      </section>

      {/* Main Footer - Light with AutoCAD Grid */}
      <footer className="bg-[#F5F5F5] grid-pattern relative overflow-hidden" data-testid="main-footer">
        {/* Decorative vectors */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-[8%] left-[3%] opacity-[0.07] text-primary"><Compass size={36} strokeWidth={1.5} style={{transform:'rotate(-20deg)'}} /></div>
          <div className="absolute top-[15%] right-[5%] opacity-[0.07] text-primary"><Target size={28} strokeWidth={1.5} style={{transform:'rotate(15deg)'}} /></div>
          <div className="absolute top-[45%] left-[2%] opacity-[0.07] text-primary"><Building size={32} strokeWidth={1.5} style={{transform:'rotate(10deg)'}} /></div>
          <div className="absolute top-[60%] right-[4%] opacity-[0.07] text-primary"><Ruler size={24} strokeWidth={1.5} style={{transform:'rotate(-30deg)'}} /></div>
          <div className="absolute top-[30%] right-[8%] opacity-[0.07] text-primary"><Layers size={30} strokeWidth={1.5} style={{transform:'rotate(25deg)'}} /></div>
          <div className="absolute top-[75%] left-[5%] opacity-[0.07] text-primary"><PenTool size={22} strokeWidth={1.5} style={{transform:'rotate(-15deg)'}} /></div>
        </div>
        <div className="container-custom py-16 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <Link to="/" className="inline-block mb-6" data-testid="footer-logo">
                <img src={LOGO_URL} alt="Land Drafters LLC" className="h-28 w-auto"  width="360" height="120" loading="lazy" />
              </Link>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                <strong className="text-foreground">We Understand Your Unique Needs!</strong><br />
                Land Drafters LLC delivers precise, reliable CAD drafting services for architects, engineers, and surveyors across the USA, Canada, the UK and Australia.
              </p>
              <div className="space-y-3">
                <a href={CONTACT_INFO.phoneLink} className="sub-link-hover inline-block relative flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors" data-testid="footer-phone">
                  <Phone size={18} className="text-primary" />
                  <span>{CONTACT_INFO.phone}</span>
                </a>
                <a href={CONTACT_INFO.phoneIndiaLink} className="sub-link-hover inline-block relative flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors" data-testid="footer-phone-india">
                  <Phone size={18} className="text-primary" />
                  <span>{CONTACT_INFO.phoneIndia}</span>
                </a>
                <a href={CONTACT_INFO.emailLink} className="sub-link-hover inline-block relative flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors" data-testid="footer-email">
                  <Mail size={18} className="text-primary" />
                  <span>{CONTACT_INFO.email}</span>
                </a>
                <a href={CONTACT_INFO.emailGenericLink} className="sub-link-hover inline-block relative flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors" data-testid="footer-email-generic">
                  <Mail size={18} className="text-primary" />
                  <span>{CONTACT_INFO.emailGeneric}</span>
                </a>
                <div className="flex items-start gap-3 text-muted-foreground">
                  <MapPin size={18} className="text-primary mt-1" />
                  <span>{CONTACT_INFO.address}</span>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h3 className="font-bold uppercase tracking-widest mb-6 text-sm text-foreground">Our Services</h3>
              <ul className="space-y-3">
                {mainServices.map((s) => (
                  <li key={s.key}>
                    <Link to={s.href} className="sub-link-hover inline-block relative text-muted-foreground hover:text-primary transition-colors text-sm">
                      {s.shortTitle}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-bold uppercase tracking-widest mb-6 text-sm text-foreground">Company</h3>
              <ul className="space-y-3">
                {aboutLinks.map((item) => (
                  <li key={item.href}>
                    <Link to={item.href} className="sub-link-hover inline-block relative text-muted-foreground hover:text-primary transition-colors text-sm">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links & Newsletter */}
            <div>
              <h3 className="font-bold uppercase tracking-widest mb-6 text-sm text-foreground">Quick Links</h3>
              <ul className="space-y-3 mb-8">
                {quickLinks.map((item) => (
                  <li key={item.href}>
                    <Link to={item.href} className="sub-link-hover inline-block relative text-muted-foreground hover:text-primary transition-colors text-sm">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>

              <h3 className="font-bold uppercase tracking-widest mb-4 text-sm text-foreground">Newsletter</h3>
              {subscribed ? (
                <p className="text-primary text-sm">Thanks for subscribing!</p>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="flex">
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className="bg-white border border-border px-4 py-2 text-sm text-foreground flex-1 focus:outline-none focus:border-primary" required data-testid="footer-newsletter-input" />
                  <button type="submit" className="bg-primary text-white px-4 py-2 hover:bg-primary/80 transition-colors" data-testid="footer-newsletter-submit">
                    <ArrowRight size={16} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        <FooterSeoLinks />

        {/* Bottom Bar */}
        <div className="border-t border-border">
          <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-muted-foreground text-sm text-center md:text-left">
              &copy; 2018–{new Date().getFullYear()} Land Drafters LLC. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="sub-link-hover inline-block relative text-muted-foreground hover:text-primary transition-colors text-sm">Privacy Policy</Link>
              <Link to="/terms" className="sub-link-hover inline-block relative text-muted-foreground hover:text-primary transition-colors text-sm">Terms of Service</Link>
            </div>
            <div className="flex items-center gap-4">
              <a href="#" className="text-muted-foreground hover:text-primary transition-colors" aria-label="LinkedIn"><Linkedin size={20} /></a>
              <a href="#" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Twitter"><Twitter size={20} /></a>
              <a href="#" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Facebook"><Facebook size={20} /></a>
              <a href="#" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
